import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Code2, Zap, Shield, Mail, Phone } from 'lucide-react';
import '../index.css';
import '../mobile-optimizations.css';

function App() {
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0.3]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);
  const canvasRef = useRef(null);

  // Particle animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 80;

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.opacity = Math.random() * 0.5 + 0.2;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        ctx.fillStyle = `rgba(0, 255, 0, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    function connectParticles() {
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            ctx.strokeStyle = `rgba(0, 255, 0, ${0.2 * (1 - distance / 150)})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
    }

    function animate() {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      connectParticles();
      requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const services = [
    {
      icon: <Code2 className="w-8 h-8" />,
      title: "Software Development",
      description: "พัฒนาระบบที่ตอบโจทย์ธุรกิจด้วยเทคโนโลยีล่าสุด"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "System Integration",
      description: "เชื่อมต่อระบบต่างๆ ให้ทำงานร่วมกันอย่างลื่นไหล"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Consulting",
      description: "ให้คำปรึกษาและออกแบบสถาปัตยกรรมระบบ"
    }
  ];

  return (
    <div className="relative min-h-screen bg-black text-white overflow-hidden scanline">
      {/* Tech Grid Background */}
      <div className="fixed inset-0 tech-grid-bg opacity-50" style={{ zIndex: 0 }} />

      {/* Animated Background Canvas */}
      <canvas
        ref={canvasRef}
        className="fixed top-0 left-0 w-full h-full pointer-events-none"
        style={{ zIndex: 1 }}
      />

      {/* Gradient Orbs - สีเขียวและฟ้าสดใส */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 2 }}>
        <motion.div
          className="absolute w-[500px] h-[500px] rounded-full blur-3xl"
          style={{
            background: 'radial-gradient(circle, rgba(0,255,0,0.15) 0%, transparent 70%)',
            left: '10%',
            top: '20%',
          }}
          animate={{
            x: [0, 100, 0],
            y: [0, -100, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute w-[500px] h-[500px] rounded-full blur-3xl"
          style={{
            background: 'radial-gradient(circle, rgba(0,255,255,0.1) 0%, transparent 70%)',
            right: '10%',
            bottom: '20%',
          }}
          animate={{
            x: [0, -100, 0],
            y: [0, 100, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Corner Tech Elements */}
      <div className="fixed top-0 left-0 w-20 h-20 border-t-2 border-l-2 border-green-500/30 pointer-events-none" style={{ zIndex: 5 }} />
      <div className="fixed top-0 right-0 w-20 h-20 border-t-2 border-r-2 border-green-500/30 pointer-events-none" style={{ zIndex: 5 }} />
      <div className="fixed bottom-0 left-0 w-20 h-20 border-b-2 border-l-2 border-green-500/30 pointer-events-none" style={{ zIndex: 5 }} />
      <div className="fixed bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-green-500/30 pointer-events-none" style={{ zIndex: 5 }} />

      {/* Main Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <motion.section
          className="min-h-screen flex items-center justify-center px-6"
          style={{ opacity, scale }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              <h1 className="text-6xl md:text-8xl font-bold mb-6 tracking-tight relative">
                <span className="inline-block bg-gradient-to-r from-green-400 via-lime-500 to-cyan-400 text-transparent bg-clip-text drop-shadow-[0_0_30px_rgba(0,255,0,0.5)]">
                  Raise You Up
                </span>
                {/* Tech accent */}
                <div className="absolute -top-4 -left-4 text-xs text-green-500/50 font-mono">{'<'}</div>
                <div className="absolute -bottom-4 -right-4 text-xs text-green-500/50 font-mono">{'/>'}</div>
              </h1>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.5 }}
                className="text-xl md:text-2xl text-green-400/80 mb-8 font-light tracking-wide font-mono"
              >
                {'> '}<span className="text-white">Crafting Quality Software Solutions</span>
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.8 }}
                className="text-gray-500 text-sm md:text-base max-w-2xl mx-auto mb-8"
              >
                Software house ที่เน้นคุณภาพและความใส่ใจในทุกรายละเอียด
              </motion.p>
            </motion.div>

            {/* Scroll Indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.2 }}
              className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
            >
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-6 h-10 border-2 border-green-500 rounded-full flex items-start justify-center p-2 shadow-[0_0_10px_rgba(0,255,0,0.5)]"
              >
                <motion.div
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-1.5 h-1.5 bg-green-500 rounded-full"
                />
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Services Section */}
        <section className="min-h-screen flex items-center justify-center px-6 py-20">
          <div className="max-w-6xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-center mb-16 relative"
            >
              <span className="bg-gradient-to-r from-green-400 to-cyan-400 text-transparent bg-clip-text drop-shadow-[0_0_20px_rgba(0,255,0,0.3)]">
                Our Expertise
              </span>
              <div className="absolute -left-8 top-1/2 -translate-y-1/2 text-green-500/30 text-2xl font-mono hidden md:block">[</div>
              <div className="absolute -right-8 top-1/2 -translate-y-1/2 text-green-500/30 text-2xl font-mono hidden md:block">]</div>
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="relative group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-cyan-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" />
                  <div className="relative bg-black/80 backdrop-blur-xl p-8 rounded-2xl border-2 border-green-500/20 hover:border-green-500 hover:shadow-[0_0_30px_rgba(0,255,0,0.3)] transition-all duration-300">
                    <div className="absolute top-2 right-2 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <div className="text-green-400 mb-4 drop-shadow-[0_0_10px_rgba(0,255,0,0.5)]">
                      {service.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-white font-mono">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="min-h-screen flex items-center justify-center px-6 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-8 relative">
                <span className="bg-gradient-to-r from-green-400 to-cyan-400 text-transparent bg-clip-text drop-shadow-[0_0_20px_rgba(0,255,0,0.3)]">
                  Let's Build Something
                </span>
                <div className="absolute -left-6 top-0 text-green-500/30 text-xl font-mono hidden md:block">{'{'}</div>
                <div className="absolute -right-6 bottom-0 text-green-500/30 text-xl font-mono hidden md:block">{'}'}</div>
              </h2>
              <p className="text-gray-400 text-lg mb-12">
                พร้อมที่จะยกระดับธุรกิจของคุณด้วยเทคโนโลยี
              </p>

              <div className="flex flex-col gap-6 justify-center items-center max-w-2xl mx-auto w-full">

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="relative group w-full"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500 via-emerald-400 to-cyan-500 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300 animate-pulse" />
                  <a
                    href="tel:020709989"
                    className="relative flex flex-col sm:flex-row items-center justify-between gap-4 px-6 sm:px-10 py-6 bg-black border-2 border-green-400 rounded-2xl text-white hover:bg-green-500/10 hover:shadow-[0_0_50px_rgba(0,255,0,0.6)] transition-all duration-300 group"
                  >
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                      <div className="w-12 h-12 flex-shrink-0 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Phone className="w-6 h-6 text-green-400 group-hover:scale-110 transition-transform" />
                      </div>
                      <div className="text-left overflow-hidden">
                        <div className="text-xs text-green-400/70 font-mono uppercase tracking-wider">สำนักงาน</div>
                        <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white tracking-wide truncate">02-070-9989</div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 mt-2 sm:mt-0 w-full sm:w-auto sm:border-l border-green-500/30 sm:pl-6 text-center sm:text-right">
                      <span className="text-sm text-cyan-400 font-mono">
                        กดเพื่อโทร →
                      </span>
                    </div>
                  </a>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="relative group w-full"
                >
                  <div className="absolute inset-0 bg-green-500/50 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" />
                  <a
                    href="mailto:teerasak.d@raiseyouup.co.th"
                    className="relative flex flex-col sm:flex-row items-center justify-between gap-4 px-6 sm:px-10 py-6 bg-black border-2 border-green-500 rounded-2xl text-white hover:bg-green-500/10 hover:shadow-[0_0_30px_rgba(0,255,0,0.5)] transition-all duration-300 group"
                  >
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                      <div className="w-12 h-12 flex-shrink-0 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Mail className="w-6 h-6 text-green-400 group-hover:scale-110 transition-transform" />
                      </div>
                      <div className="text-left overflow-hidden">
                        <div className="text-xs text-green-400/70 font-mono uppercase tracking-wider">อีเมล</div>
                        <div className="text-lg sm:text-xl md:text-2xl font-bold text-white tracking-wide overflow-hidden text-ellipsis sm:whitespace-nowrap">teerasak.d@raiseyouup.co.th</div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 mt-2 sm:mt-0 w-full sm:w-auto sm:border-l border-green-500/30 sm:pl-6 text-center sm:text-right">
                      <span className="text-sm text-green-400 font-mono">
                        ส่งอีเมล →
                      </span>
                    </div>
                  </a>
                </motion.div>

              </div>
            </motion.div>

            {/* Footer */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.4 }}
              viewport={{ once: true }}
              className="mt-20 pt-12 border-t border-green-500/20"
            >
              <p className="text-gray-500 text-sm font-mono">
                <span className="text-green-500/50">{'// '}</span>© 2025 Raise You Up Solution. All rights reserved.
              </p>
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default App;
